import { forwardRef, useRef, useCallback } from 'react';
import { ClipLoader } from 'react-spinners';
import styled from '@emotion/styled';
import { css } from '@emotion/core';
import useToggle from 'react-use/lib/useToggle';
import useClickAway from 'react-use/lib/useClickAway';
import { colorsButton } from 'app/colors';
import { applyRef } from 'components/Inputs/props';
import Select from 'components/Kizen/Select';
import Icon from 'components/Kizen/Icon';
import Button from '.';

const Wrapper = styled.div`
  position: relative;
  display: inline-flex;
  ${({ noSpace = false }) =>
    !noSpace &&
    css`
      margin: 0.5rem 0.75rem;
    `}
  button:first-of-type {
    flex: 1;
    margin: 0;
    border-top-right-radius: 0;
    border-bottom-right-radius: 0;
  }
  button:last-of-type {
    margin: 0;
    border-top-left-radius: 0;
    border-bottom-left-radius: 0;
  }
`;

const VerticalDivider = styled.div`
  width: 1px;
  ${({ color, disabled }) => {
    if (disabled) {
      return css`
        background-image: linear-gradient(
          to bottom,
          var(--color-background-dark) 0%,
          var(--color-background-medium) 100%
        );
      `;
    }
    const colors = colorsButton[color];
    if (!colors) {
      return css``;
    }
    return css`
      background-image: linear-gradient(
        to bottom,
        ${colors.hover} 0%,
        ${colors.default} 100%
      );
    `;
  }}
`;

const ButtonMenu = styled(Select)`
  > :first-child {
    ${({ restrictWidth }) =>
      restrictWidth
        ? css`
            width: 100%;
          `
        : null}

    position: absolute;

    ${({ bottomOffset }) =>
      bottomOffset
        ? css`
            bottom: ${bottomOffset}px;
          `
        : css`
            bottom: 0;
          `}
    right: 0;
  }
  .ButtonMenu__control {
    display: none;
  }
  .ButtonMenu__menu {
    position: absolute;
    top: 0;
    right: 0;
    width: auto;
    max-width: 100%;
    border: 1px solid var(--color-background-medium);
    border-radius: 4px;
  }
`;

const ButtonWithDropdown = forwardRef(
  (
    {
      className,
      style,
      color = 'green',
      disabled = false,
      options,
      onClick,
      onChange,
      children,
      variant = 'default',
      loading = false,
      loadingColor = 'var(--color-background-white)',
      restrictWidth = true,
      clickToOpen = false,
      clickNotify = null,
      bottomOffset = '0',
      ...others
    },
    ref
  ) => {
    const [open, toggleOpen] = useToggle(false);
    const dropdownRef = useRef(null);
    useClickAway(dropdownRef, () => toggleOpen(false));
    const isDisabled = disabled || loading;

    const mergeRef = useCallback(
      (node) => {
        dropdownRef.current = node;
        applyRef(ref, node);
      },
      [ref]
    );

    const handleClick = useCallback(
      (ev) => {
        if (clickToOpen) {
          toggleOpen();
        } else {
          toggleOpen(false);
          if (!open && onClick) {
            onClick(ev);
          }
        }
        if (clickNotify) clickNotify();
      },
      [open, toggleOpen, onClick, clickToOpen, clickNotify]
    );

    const handleChange = useCallback(
      (value) => {
        toggleOpen(false);
        if (onChange) {
          onChange(value);
        }
        if (clickNotify) clickNotify();
      },
      [toggleOpen, onChange, clickNotify]
    );

    const handleToggleOpen = useCallback(() => {
      toggleOpen();
      if (clickNotify) clickNotify();
    }, [toggleOpen, clickNotify]);

    return (
      <Wrapper ref={mergeRef} className={className} style={style} {...others}>
        <Button
          variant={variant}
          color={color}
          disabled={isDisabled}
          onClick={handleClick}
        >
          {loading && <ClipLoader loading size={20} color={loadingColor} />}
          {!loading && children}
        </Button>
        {variant === 'default' && (
          <VerticalDivider color={color} disabled={isDisabled} />
        )}
        <Button
          noMinWidth
          variant={variant}
          color={color}
          disabled={isDisabled}
          onClick={handleToggleOpen}
        >
          <Icon icon="down" color="inherit" />
        </Button>
        {open && !isDisabled && (
          <ButtonMenu
            menuInLine
            classNamePrefix="ButtonMenu"
            menuIsOpen
            menuPlacement="bottom"
            filterOption={null}
            hideSelectedOptions={false}
            options={options}
            onChange={handleChange}
            restrictWidth={restrictWidth}
            bottomOffset={bottomOffset}
            {...others}
          />
        )}
      </Wrapper>
    );
  }
);

export default ButtonWithDropdown;
